import { useState, FC, Fragment, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import Formate from "./Formate";
import CPFormatter from "Utils/CP.Formatter";
import PatientFormatter from "Utils/Patient.Formatter";
import CarePlanApi from "Service/CarePlan.api";
import PatientApi from "Service/Patient.api";
import {
  setAlertData,
  setRightHead,
  setTimerStatus,
  startLoading,
  stopLoading,
} from "Redux/Screen/Action";
import { addSelectedPrgm } from "Redux/Patient/Action";
import {
  PatientInitialStateType,
  ScreenInitialStateType,
} from "DataTypes/Redux";
import {
  CPCatQuesOptType,
  CPQuesOptType,
  HandleQuestionUpdateProps,
  PrblmActivePlan,
} from "DataTypes/PatientManagement.type";
import { CPApiCatType } from "DataTypes/Services/Careplan.type";
import { RouteUrls } from "Constant/RouteUrls";
import CategoryBox from "./CategoryBox";
import QuestionBox from "./QuestionBox";
import ProblemBox from "./ProblemBox";
import CPPreApprovedPlanDetails from "./CPPreApprovedPlanDetails";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import { CheckStringEmpty } from "Utils/common";

const CarePlanQuestionnaire: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    rightHead: { timerStatus },
  }: ScreenInitialStateType = useSelector((state: any) => state?.screen);
  const { selectedPrgm }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const careId = selectedPrgm.careplanId;
  const recurringId = selectedPrgm.careplanRecurringId;
  const currentUrl = window.location.href;
  const isRecurring = currentUrl.includes(RouteUrls.recurring);
  const prgmName = selectedPrgm.abbrevation;
  const isTCM = prgmName == "TCM";
  const state = isRecurring ? "recurring" : "careplan";
  const readOnly = selectedPrgm
    ? selectedPrgm.readOnlyArr.some((el) => el == "question")
    : true;
  const [catQuesOptArr, setCatQuesOptArr] = useState<CPCatQuesOptType[]>([]);
  const [selectedCat, setSelectedCat] = useState<CPApiCatType | null>(null);
  const [loadingOptQuesId, setLoadingOptQuesId] = useState<number | null>(null);
  const [loadingCat, setLoadingCat] = useState(true);
  const [loadingProb, setLoadingProb] = useState(true);
  const [problemArr, setProblemArr] = useState<string[]>([]);
  const [openQuestionIds, setOpenQuestionIds] = useState<(number | null)[]>([]);
  const [localInput, setLocalInput] = useState("");
  const [pressedNext, setPressedNext] = useState(false);
  const [selectedQues, setSelectedQues] = useState<number | null>(null);
  const [selectedProb, setSelectedProb] =
    useState<PrblmActivePlan | null>(null);
  const [newPrblmList, setNewPrblmList] = useState<PrblmActivePlan[]>([]);
  const [pAPPrblmArr, setPAPPrblmArr] = useState<(number | null)[]>([]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handleOnLoadCalls();
  }, [pAPPrblmArr]);

  useEffect(() => {
    if (selectedProb) {
      setSelectedCat(null);
      setOpenQuestionIds([])
      setSelectedQues(null)
    }
  }, [selectedProb]);

  useEffect(() => {
    selectedCat && setSelectedProb(null);
  }, [selectedCat])

  useEffect(() => {
    handleProblemIcdList();
    isTCM ? handleTCMProblemsLoad() : handleProblemsLoad();
    handleUpdateStatus();
    handleTimer();
  }, [careId, recurringId]);

  const handleTimer: () => void = () => {
    if (selectedPrgm.showLogTime) {
      if (timerStatus == 1) {
        dispatch(setTimerStatus({ status: 2, parentApiCall: true }));
      }
      if (prgmName == "RPM") dispatch(setRightHead("care-plan"));
    }
  }

  const handleOnLoadCalls: () => void = async () => {
    setLoadingCat(true);
    const newArr = await handleQuesApiCall(null, null);
    const newCatArr = Formate.SortCategoryQuestion(newArr, []);
    const newCatArr1 = Formate.CheckSelectedCategory(
      newCatArr,
      selectedCat?.categoryId || null
    );
    const newObj = Formate.CheckAllCategory(newCatArr1);
    setCatQuesOptArr(newObj.catArr);
    if (newObj?.catArr?.length > 0) {
      setSelectedCat(newObj.catArr[0]);
    }
    setLoadingCat(false);
  };

  const handleQuesApiCall: (
    parentQuestionId: number | null,
    parentOptionId: number | null
  ) => Promise<CPCatQuesOptType[]> = async (
    parentQuestionId,
    parentOptionId
  ) => {
    const payload = {
      parentQuestionId,
      parentOptionId,
      state,
      careplanRecurringId: recurringId,
      problemIds: pAPPrblmArr
    };
    const res = await CarePlanApi.QuestionSearch({
      data: payload,
      id1: careId,
    });
    if (res?.success) return Formate.QuestionList(res?.data);
    else return [];
  };

  const handleTCMProblemsLoad: () => void = async () => {
    setLoadingProb(true);
    const res = await PatientApi.DischargeSummarySearch({
      id1: careId,
    });
    if (res?.success) {
      if (res.data.length) {
        const formattedData = PatientFormatter.DischargeSummary(res?.data[0]);
        setProblemArr(
          [
            ...(formattedData.primaryDiagnosis
              ? [formattedData.primaryDiagnosis]
              : []),
            ...(formattedData.secondaryDiagnosis
              ? [formattedData.secondaryDiagnosis]
              : []),
            ...(formattedData.tertiaryDiagnosis
              ? [formattedData.tertiaryDiagnosis]
              : []),
          ].sort()
        );
      }
    }
    setLoadingProb(false);
  };

  const handleProblemsLoad: () => void = async () => {
    setLoadingProb(true);
    const probRes = await CarePlanApi.careplanProblemsRead({
      id1: careId,
    });
    if (probRes?.success) {
      const newArr = CPFormatter.CheckCPProblem(probRes?.data?.careplanProblem);
      //Client changes PMS task id: #13853
      // if (isRPM) setProblemArr(Formate.FormatRPMProblems(newArr));
      // else setProblemArr(Formate.FormatProblems(newArr));
      setProblemArr(Formate.FormatRPMProblems(newArr));
    }
    setLoadingProb(false);
  };

  const handleUpdateStatus: () => void = async () => {
    if (isRecurring) {
      const payload = { hasCareplanRecurringQuestionnaireCompleted: false };
      await CarePlanApi.RecurringCpQuestionStatusUpdate(
        {
          id1: recurringId,
          data: payload,
        }
      );
    }
  };

  const handleCategoryChange: (obj: CPApiCatType | null) => void = async (
    obj
  ) => {
    if (obj) {
      const newCatArr1 = Formate.CheckSelectedCategory(
        catQuesOptArr,
        obj.categoryId
      );
      setCatQuesOptArr(newCatArr1);
    }
    setSelectedCat(obj);
  };

  const handleQuestionClick: (question: CPQuesOptType) => void = async (
    question
  ) => {
    setSelectedQues(question.questionsId);
    const find = openQuestionIds.find((el) => el == question.questionsId);
    if (find) {
      setOpenQuestionIds(
        openQuestionIds.filter((el) => el != question.questionsId)
      );
    } else {
      setLoadingOptQuesId(question.questionsId);
      setOpenQuestionIds([...openQuestionIds, question.questionsId]);
      if (
        question.options.length == 0 &&
        (question.questionType == "singleSelect" ||
          question.questionType == "multiSelect")
      ) {
        const newCatArr = await handleOptApiCall(
          question.questionsId,
          catQuesOptArr
        );
        const newCatArr1 = Formate.CheckSelectedCategory(
          newCatArr,
          selectedCat?.categoryId || null
        );
        setCatQuesOptArr(newCatArr1);
      }
      setLoadingOptQuesId(null);
    }
  };

  const handleOptApiCall: (
    id: number | null,
    categoryArr: CPCatQuesOptType[]
  ) => Promise<CPCatQuesOptType[]> = async (id, categoryArr) => {
    const newcategoryArr: CPCatQuesOptType[] = JSON.parse(
      JSON.stringify(categoryArr)
    );
    setLoadingOptQuesId(id);
    const payload = {
      questionId: id,
      state,
      careplanRecurringId: recurringId,
    };
    const res = await CarePlanApi.QuesOptSearch({
      data: payload,
      id1: careId,
    });
    if (res?.success) {
      setLoadingOptQuesId(null);
      const optionArr = CPFormatter.CheckCarePlanQuesOpt(res?.data);
      const catIndex = newcategoryArr.findIndex(
        (el) => el.categoryId == selectedCat?.categoryId
      );
      if (catIndex != -1) {
        const quesIndex = newcategoryArr[catIndex].questionList.findIndex(
          (el) => el.questionsId == id
        );
        if (quesIndex != -1)
          newcategoryArr[catIndex].questionList[quesIndex].options = optionArr;
      }
      let newcategoryArr1: CPCatQuesOptType[] = JSON.parse(
        JSON.stringify(newcategoryArr)
      );
      const filterOptArr = optionArr.filter((optEl) => optEl.answered);
      await (async function loop() {
        for (let i = 0; i < filterOptArr.length; i++) {
          const findOptToQues = newcategoryArr[catIndex].questionList.find(
            (quesEl) => quesEl.parentOptionId == filterOptArr[i].id
          );
          if (filterOptArr[i].answered && !findOptToQues) {
            const newSet = await handleQuesApiCall(id, filterOptArr[i].id);
            newcategoryArr1 = Formate.SortCategoryQuestion(
              newcategoryArr1,
              newSet
            );
          }
        }
      })();
      return newcategoryArr1;
    } else {
      setLoadingOptQuesId(null);
      return newcategoryArr;
    }
  };

  const handleOptionClick: (
    question: CPQuesOptType,
    optId: number | null
  ) => void = async (question, optId) => {
    setSelectedQues(question.questionsId)
    const newQuestion: CPQuesOptType = JSON.parse(JSON.stringify(question));
    if (newQuestion.questionType == "multiSelect") {
      const OptIndex = newQuestion.options.findIndex((el) => el.id == optId);
      newQuestion.options[OptIndex].answered =
        !newQuestion.options[OptIndex].answered;
    } else if (newQuestion.questionType == "singleSelect") {
      const findCpOptionId = newQuestion.options.find(
        (el) => el.careplanQuestionOptionId
      );
      const newOptArr = newQuestion.options.map((el) => {
        if (el.id == optId) {
          el.answered = !el.answered;
          el.careplanQuestionOptionId = findCpOptionId?.careplanQuestionOptionId
            ? findCpOptionId?.careplanQuestionOptionId
            : null;
        } else el.answered = false;
        return el;
      });
      newQuestion.options = newOptArr;
    }
    dispatch(startLoading({ loading: true, loadtext: "Loading" }));
    const updatedArr = await handleQuestionUpdate({
      question: newQuestion,
      categoryArr: catQuesOptArr,
      optId,
    });
    const newCatArr1 = Formate.CheckSelectedCategory(
      updatedArr,
      selectedCat?.categoryId || null
    );
    setCatQuesOptArr(newCatArr1);
    dispatch(stopLoading());
  };

  const handleChangeText: (
    questionId: number | null,
    text: string,
    textType: "answer" | "comment"
  ) => void = (questionId, text, textType) => {
    setSelectedQues(questionId)
    const newcategoryArr: CPCatQuesOptType[] = JSON.parse(
      JSON.stringify(catQuesOptArr)
    );
    const catIndex = newcategoryArr.findIndex(
      (el) => el.categoryId == selectedCat?.categoryId
    );
    if (catIndex != -1) {
      const quesIndex = newcategoryArr[catIndex].questionList.findIndex(
        (el) => el.questionsId == questionId
      );
      if (quesIndex != -1) {
        if (textType == "comment")
          newcategoryArr[catIndex].questionList[quesIndex].questionComment =
            text;
        else
          newcategoryArr[catIndex].questionList[quesIndex].freeTextAnswer =
            text;
      }
    }
    const newCatArr1 = Formate.CheckSelectedCategory(
      newcategoryArr,
      selectedCat?.categoryId || null
    );
    setCatQuesOptArr(newCatArr1);
  };

  const handleQuestionUpdate: (
    props: HandleQuestionUpdateProps
  ) => Promise<CPCatQuesOptType[]> = async ({
    question,
    categoryArr,
    optId,
    oldComment,
    oldAnswer,
  }) => {
    const newcategoryArr: CPCatQuesOptType[] = JSON.parse(
      JSON.stringify(categoryArr)
    );
    const filterOption = question.options.filter((el) => el.answered);
    const isRequiredOptionsAnswered = question.options.find(
      (el) => !el.answered && el.requiredOption
    )
      ? false
      : true;
    const formateOptionArr = filterOption.map((el) => {
      return {
        ...(el.careplanQuestionOptionId && {
          id: el.careplanQuestionOptionId,
        }),
        optionId: el.id,
        optionText: el.option,
      };
    });
    const payload = {
      careplanQuestionId: question.careplanQuestionId,
      questionId: question.questionsId,
      questionText: question.question,
      questionComment: question.questionComment.trim() || null,
      freeTextAnswer: question.freeTextAnswer.trim() || null,
      categoryId: selectedCat?.categoryId,
      categoryText: selectedCat?.category,
      programId: question.programId,
      options: formateOptionArr,
      careplanRecurringId: recurringId,
      state,
      isRequiredOptionsAnswered,
      isChildRequiredOptionsAnswered: false,
      hasChidAnswered: false,
    };
    const res = await CarePlanApi.QuesUpdate({
      data: payload,
      id1: careId,
    });
    const catIndex = newcategoryArr.findIndex(
      (el) => el.categoryId == selectedCat?.categoryId
    );
    if (catIndex != -1) {
      const quesIndex = newcategoryArr[catIndex].questionList.findIndex(
        (el) => el.questionsId == question.questionsId
      );
      if (quesIndex != -1) {
        if (res?.success) {
          question.isRequiredOptionAnswered = isRequiredOptionsAnswered;
          const newQuesObj = Formate.FormateUpdateQuestion(res?.data, question);
          newcategoryArr[catIndex].questionList[quesIndex] = newQuesObj;
          if (optId) {
            const unAnsOptIds = newQuesObj.options
              .filter((opt) => !opt.answered)
              .map((opt1) => opt1.id);
            const { quesList, openArr } = await Formate.FilterOutQuestions(
              unAnsOptIds,
              newcategoryArr[catIndex].questionList,
              openQuestionIds
            );
            setOpenQuestionIds(openArr);
            newcategoryArr[catIndex].questionList = quesList;
            if (newQuesObj.options.find((el) => el.id == optId)?.answered) {
              const newQuesSet = await handleQuesApiCall(
                question.questionsId,
                optId
              );
              return Formate.SortCategoryQuestion(newcategoryArr, newQuesSet);
            } else return newcategoryArr;
          } else return newcategoryArr;
        } else {
          if (oldComment) {
            const newQuesObj: CPQuesOptType = JSON.parse(
              JSON.stringify(question)
            );
            newQuesObj.questionComment = oldComment;
            newcategoryArr[catIndex].questionList[quesIndex] = newQuesObj;
          }
          if (oldAnswer) {
            const newQuesObj: CPQuesOptType = JSON.parse(
              JSON.stringify(question)
            );
            newQuesObj.freeTextAnswer = oldAnswer;
            newcategoryArr[catIndex].questionList[quesIndex] = newQuesObj;
          }
          return newcategoryArr;
        }
      } else return newcategoryArr;
    } else return newcategoryArr;
  };

  const handleUpdateText: (
    question: CPQuesOptType,
    localText: string,
    textType: "answer" | "comment"
  ) => void = async (question, localText, textType) => {
    setLocalInput("");
    dispatch(startLoading({ loading: true, loadtext: "Loading" }));
    const updatedArr = await handleQuestionUpdate({
      question,
      categoryArr: catQuesOptArr,
      oldComment: textType == "comment" ? localText : "",
      oldAnswer: textType == "answer" ? localText : "",
    });
    const newCatArr1 = Formate.CheckSelectedCategory(
      updatedArr,
      selectedCat?.categoryId || null
    );
    setCatQuesOptArr(newCatArr1);
    dispatch(stopLoading());
  };

  const handleProblemIcdList: () => void = async () => {
    const res = await PreApprovedPlanApi.PrblmWithActivePlanList({
      id1: careId
    });
    let newArr: PrblmActivePlan[] = []
    if (res?.success) {
      const formattedData =
        PreApprovedPlanFormatter.PrblmActivePlanList(res?.data);
      newArr = formattedData;
    }
    setNewPrblmList(newArr);
    const findIndex = newArr.findIndex(el => el.isPlanInUse);
    if (findIndex != -1) setSelectedProb(newArr[findIndex])
    setPAPPrblmArr(newArr.filter(el => el.isPlanInUse).map(el => el.problemId));
  };

  const handleTogglePAP: (
    prblmData: PrblmActivePlan
  ) => void = async (prblmData) => {
    const isPlanInUse = prblmData.isPlanInUse;
    const newPrblmData: PrblmActivePlan = {
      ...prblmData,
      isPlanInUse: !isPlanInUse,
      isPlanEdited: isPlanInUse ? false : prblmData.isPlanEdited
    }
    let res;
    if (isPlanInUse) {
      const payload = {
        problemId: prblmData.id,
        carePlanId: careId
      }
      res = await PreApprovedPlanApi.DeletePAPForCP({ data: payload });
    } else {
      const payload = {
        activePlanId: prblmData.activePlanId,
        careplanId: careId
      }
      res = await PreApprovedPlanApi.CreatePAPForCP({ data: payload });
    }
    if (res?.success) {
      setSelectedProb(newPrblmData);
      const prblmArr = newPrblmList.map(prblmEl => {
        if (prblmEl.id == prblmData.id) prblmEl = newPrblmData;
        return prblmEl
      })
      setNewPrblmList(prblmArr);
      setPAPPrblmArr(
        prblmArr.filter(el => el.isPlanInUse).map(el => el.problemId)
      )
    }
  };

  const handleEditPAP: (
    prblmData: PrblmActivePlan
  ) => void = async (prblmData) => {
    if (!prblmData.isPlanEdited) {
      const payload = {
        problemId: prblmData.id,
        careplanId: careId
      }
      const res = await PreApprovedPlanApi.EditPAPForCP({ data: payload });
      if (res?.success) {
        setNewPrblmList(newPrblmList.map(prblmEl => {
          if (prblmEl.id == prblmData.id) prblmData.isPlanEdited = true;
          return prblmEl
        }))
      }
    }
  };

  const handleSave: () => void = async () => {
    setPressedNext(true);
    if (readOnly) {
      if (isRecurring)
        navigate(`../${RouteUrls.careSummary}`, { replace: true });
      else navigate(`../${RouteUrls.careSummary}`, { replace: true });
    } else {
      const newObj = Formate.CheckAllCategory(catQuesOptArr);
      setCatQuesOptArr(newObj.catArr);
      if (newObj.allCatSuccess) {
        dispatch(startLoading({ loading: true, loadtext: "Loading" }));
        if (isRecurring) {
          const payload = { hasCareplanRecurringQuestionnaireCompleted: true };
          const updateStatusRes =
            await CarePlanApi.RecurringCpQuestionStatusUpdate({
              id1: recurringId,
              data: payload,
            });
          if (updateStatusRes?.success)
            navigate(`../${RouteUrls.careSummary}`, { replace: true });
        } else {
          const payload = { hasCareplanQuestionnaireCompleted: true };
          const updateStatusRes = await CarePlanApi.CpQuestionStatusUpdate({
            id1: careId,
            data: payload,
          });
          if (updateStatusRes?.success) {
            const newState =
              CheckStringEmpty(updateStatusRes?.data?.carePlanState);
            selectedPrgm &&
              dispatch(
                addSelectedPrgm({
                  ...selectedPrgm,
                  careplanState: newState,
                  careplanStage: PatientFormatter.GetCPStage(
                    newState
                  ),
                  hasCareplanQuestionnaireCompleted: true,
                })
              );
            navigate(`../${RouteUrls.careSummary}`, { replace: true });
          }
        }
        dispatch(stopLoading());
      } else {
        dispatch(
          setAlertData({
            alertMessage: "Please answer all required questions",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    }
  };

  return (
    <Fragment>
      <Box width="25%" height="100%">
        <CategoryBox
          catQuesOptArr={catQuesOptArr}
          handleSelectCat={handleCategoryChange}
          selectedCat={selectedCat}
          loadingCat={loadingCat}
          pressedNext={pressedNext}
        />
        <Box height="3%" />
        <ProblemBox
          loadingProblem={loadingProb}
          problemArr={problemArr}
          handleSelectProb={(data) => setSelectedProb(data)}
          newPrblmList={newPrblmList}
          handleTogglePAP={handleTogglePAP}
          readOnly={readOnly}
          isRecurring={isRecurring}
          isTCM={isTCM}
        />
      </Box>
      {selectedCat && !selectedProb ?
        (
          <QuestionBox
            catQuesOpt={
              catQuesOptArr.find(
                (el) => el.categoryId == selectedCat.categoryId
              ) || null
            }
            handleQuestionClick={handleQuestionClick}
            openQuestionIds={openQuestionIds}
            handleOptionClick={handleOptionClick}
            handleChangeText={handleChangeText}
            handleUpdateText={handleUpdateText}
            handleSave={handleSave}
            loadingOptQuesId={loadingOptQuesId}
            setLocalInput={(val, questionId) => {
              setLocalInput(val);
              setSelectedQues(questionId);
            }}
            localInput={localInput}
            readOnly={readOnly}
            selectedPrgm={selectedPrgm}
            pressedNext={pressedNext}
            selectedQuesId={selectedQues}
            isRecurring={isRecurring}
          />
        )
        :
        (
          selectedProb &&
          <CPPreApprovedPlanDetails
            problemData={selectedProb}
            handleUsePAP={handleTogglePAP}
            handleEditPAP={handleEditPAP}
            handleSave={handleSave}
            readOnly={readOnly}
            isRecurring={isRecurring}
          />
        )}
    </Fragment>
  );
};
export default CarePlanQuestionnaire;
